import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { grey } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import Chip from "../chip/Chip";
import Grid from "../grid/Grid";
import { useLocale } from "designSystem/locale";
import Text, { TextProps } from "../text/Text";

const PREFIX = "ChipArray";

const classes = {
	chip: `${PREFIX}chip`,
	chipDefault: `${PREFIX}chipDefault`,
	chipName: `${PREFIX}chipName`,
	root: `${PREFIX}root`,
};

const Root = styled("div")(({
	theme,
}) => ({
	[`& .${classes.chip}`]: {
		backgroundColor: theme.palette.background.paper,
		border: "unset",
		margin: theme.spacing(0.5),
		maxWidth: 200,
	},

	[`& .${classes.chipDefault}`]: {
		border: "unset",
		margin: theme.spacing(0.5),
		maxWidth: 200,
	},

	[`& .${classes.chipName}`]: {
		color: grey.A100,
		paddingRight: 4,
	},

	[`& .${classes.root}`]: {
		backgroundColor: grey[300],
		borderRadius: 15,
		minHeight: 28,
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(1),
	},
}));

export interface ChipArrayProps<T extends ComponentItem> {
	/** maximum width of each chip in array */
	chipMaxWidth?: number;

	/** item array */
	items: T[];

	/** name for this chip array */
	name: string;

	/** properties for this name section */
	nameProps?: Omit<TextProps, "bold" | "style" | "text">;

	/** callback for delete single chip */
	onDeleteItem?: (item: T) => void;

	/** default is white background and grey pills, and outlined is grey background and white pills */
	variant?: "default" | "outlined";
}

const ChipArray = <T extends ComponentItem>(props: ChipArrayProps<T>) => {
	const { chipMaxWidth, items, name, nameProps, onDeleteItem, variant } = props;
	const _variant = variant === "default" ? "filled" : "outlined";
	const bOutlined = _variant === "outlined";

	const [chipData, setChipData] = useState<T[]>(items);

	// handle delete single chip
	const handleDelete = (chipToDelete: T) => () => {
		setChipData((chips) => chips.filter((chip) => chip.id !== chipToDelete.id));

		// callback
		onDeleteItem?.(chipToDelete);
	};

	useEffect(() => {
		// sync chipData with items, make sure chipData is the latest
		setChipData(items);
	}, [items]);

	const loc = useLocale();

	return (
		<Root>
			{chipData.length > 0 && (
				<Grid
					alignItems="center"
					className={bOutlined ? classes.root : ""}
					container
				>
					{name && (
						<Grid item>
							<Text
								bold
								className={classes.chipName}
								text={name}
								{...nameProps}
							/>
						</Grid>
					)}
					{chipData.map((item, i) => (
						<Grid item key={i}>
							<Chip
								className={bOutlined ? classes.chip : classes.chipDefault}
								label={item.name || loc.resource("Blanks")}
								onDelete={handleDelete(item)}
								style={chipMaxWidth ? { maxWidth: chipMaxWidth } : {}}
								variant={_variant}
							/>
						</Grid>
					))}
				</Grid>
			)}
		</Root>
	);
};

export default ChipArray;